import animationsConfig, { aliasBrandMap } from "../config/animations.config";
import type { Plugin } from "../core/plugin";
import { PluginRegistry } from "../core/pluginRegistry";

export class Animations implements Plugin {
  private animatedElements: HTMLElement[] = [];
  private ticking = false;
  private handleScrollBound = this.handleScroll.bind(this);
  private offset = 250;

  constructor(private element: HTMLElement) {}

  private getAnimationsConfig(key: string | null) {
    if (!key) return undefined;

    const lowerKey = key.toLowerCase();
    const alias = aliasBrandMap[lowerKey];
    const finalKey = alias || key.toUpperCase();

    return animationsConfig[finalKey];
  }

  init(): void {
    const webcode = document.body.getAttribute("data-webcode");
    const brand = document.body.getAttribute("data-brand") ?? "";
    const currentBrand = webcode ?? brand;
    const config = this.getAnimationsConfig(currentBrand);
    if (!config) {
      document.body.classList.add("no-animations");
      return;
    }

    const classMap = config.classMap;
    const offset = config.offset ?? 250;
    this.offset = offset;

    Object.entries(classMap).forEach(([className, animType]) => {
      try {
        const el = this.element.querySelector<HTMLElement>(
          `.${CSS.escape(className)}`,
        );
        if (el) {
          el.classList.add(animType);
          el.classList.remove("visible");
          this.animatedElements.push(el);
        }
      } catch (e) {
        console.warn("[Animations] id invalid:", className, e);
      }
    });

    // Scroll handling
    window.addEventListener("scroll", this.handleScrollBound, {
      passive: true,
    });
    this.handleScroll();
  }

  destroy?(): void {
    window.removeEventListener("scroll", this.handleScrollBound);
  }

  private handleScroll(): void {
    if (!this.ticking) {
      window.requestAnimationFrame(() => this.updateAnimations());
      this.ticking = true;
    }
  }

  private updateAnimations(): void {
    const viewportHeight = window.innerHeight;
    const remaining: HTMLElement[] = [];

    this.animatedElements.forEach((el) => {
      const rect = el.getBoundingClientRect();
      const isVisible =
        rect.top < viewportHeight - this.offset && rect.bottom > 0;

      if (isVisible) {
        el.classList.add("visible");
      } else {
        remaining.push(el);
      }
    });

    this.animatedElements = remaining;
    this.ticking = false;

    if (this.animatedElements.length === 0) {
      window.removeEventListener("scroll", this.handleScrollBound);
    }
  }
}

PluginRegistry.register("animations", Animations);
